import { useEffect, useState } from 'react';
import { Lazy, Navigation, Pagination, Keyboard } from 'swiper';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react/swiper-react';

import VideoPlayer from './VideoPlayer';

// “official” imports like `swiper/css` do not resolve properly, check
// Swiper’s documentation when changing something
import 'swiper/swiper.min.css';
import 'swiper/modules/navigation/navigation.min.css';
import 'swiper/modules/pagination/pagination.min.css';

import './Slider.css';

interface SliderProps {
  sliderId: string;
  useKeyboard?: boolean;
  usePagination?: boolean;
  images: {
    id: number;
    url: string;
  }[];
  videos?: {
    id: number;
    streamUrl: string;
    posterUrl?: string;
  }[];
}

interface SwiperUpdateHandlerProps {
  sliderId: string;
}

function SwiperUpdateHandler({ sliderId }: SwiperUpdateHandlerProps): null {
  const [initialLoad, setInitialLoad] = useState(true);
  const swiper = useSwiper();

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }

    if (swiper) {
      swiper.update();
      swiper.slideTo(1);
      swiper.lazy.load();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiper, sliderId]);

  return null;
}

export default function Slider({
  sliderId,
  useKeyboard,
  usePagination,
  images,
  videos,
}: SliderProps): JSX.Element {
  return (
    <Swiper
      className={`slider ${sliderId}`}
      modules={[Lazy, Navigation, Pagination, Keyboard]}
      loop
      lazy={{ loadPrevNext: true }}
      keyboard={useKeyboard}
      pagination={usePagination ? { dynamicBullets: true } : false}
      navigation
    >
      {videos &&
        videos.map((video) => (
          <SwiperSlide key={video.id}>
            <div className="slider__image-wrapper">
              <VideoPlayer
                src={video.streamUrl}
                poster={video.posterUrl}
                className="swiper-lazy slider__image"
              />
            </div>
          </SwiperSlide>
        ))}
      {images.map((image) => (
        <SwiperSlide key={image.id}>
          <div className="slider__image-wrapper">
            <img data-src={image.url} alt="" className="swiper-lazy slider__image" />
          </div>
        </SwiperSlide>
      ))}
      <SwiperUpdateHandler sliderId={sliderId} />
    </Swiper>
  );
}

const defaultProps: Pick<SliderProps, 'useKeyboard' | 'usePagination' | 'videos'> = {
  useKeyboard: false,
  usePagination: false,
  videos: undefined,
};

Slider.defaultProps = defaultProps;
